import {
  CRISIS_DIRECTOR,
  CRISIS_KIND_IDS,
  INSTABILITY,
  INSTABILITY_SOURCE_IDS,
  OUTPOSTS,
  type CrisisKindId,
  type InstabilitySourceId,
  type StarClassId,
} from '@sd/content';
import type { OutpostState } from './outposts';
import type { Rng } from './rng';
import type { StarSystem } from './system';

/**
 * The instability index and the crisis director (GDD §9). Pure helpers;
 * the match drives them from `step`.
 */
export type SourceTotals = Readonly<Record<InstabilitySourceId, number>>;

export interface InstabilityState {
  /** 0..100, visible to everyone. */
  readonly value: number;
  /** What drove the index up since the last crisis was planned: picks its kind. */
  readonly sinceCrisis: SourceTotals;
  /** What drove it up over the whole match. */
  readonly total: SourceTotals;
  /** Thresholds already acted on (planned or skipped). */
  readonly thresholdsPassed: number;
  readonly finalPlanned: boolean;
}

/** A crisis on its way: planned, then announced, then it strikes (#29 deals the blow). */
export interface Crisis {
  readonly id: number;
  readonly kind: CrisisKindId;
  /** Direction from the star it comes from, 0..5 (hex directions); null for the whole system. */
  readonly sector: number | null;
  /** The source that drove the index most since the last crisis. */
  readonly cause: InstabilitySourceId;
  readonly plannedAt: number;
  /** Everyone hears of it at this tick; players with foresight earlier. */
  readonly announceAt: number;
  readonly strikeAt: number;
  /** Players already warned. */
  readonly warned: readonly string[];
  /** Phase of the final crisis, 1..CRISIS_DIRECTOR.finalPhases; null for a regular one. */
  readonly phase: number | null;
}

const zero = (): Record<InstabilitySourceId, number> =>
  Object.fromEntries(INSTABILITY_SOURCE_IDS.map((s) => [s, 0])) as Record<
    InstabilitySourceId,
    number
  >;

export const createInstability = (): InstabilityState => ({
  value: 0,
  sinceCrisis: zero(),
  total: zero(),
  thresholdsPassed: 0,
  finalPlanned: false,
});

/** Whether an outpost mines an asteroid: a find's asteroid or a planet of the belt. */
export const onAsteroid = (system: StarSystem, o: OutpostState): boolean =>
  o.id.startsWith('asteroid-') ||
  system.planets.some((p) => p.id === o.id && p.kind === 'asteroids');

export interface InstabilityInputs {
  readonly debrisMetals: number;
  /** Outposts producing this cycle. */
  readonly outposts: readonly OutpostState[];
  readonly system: StarSystem;
  /** Finished megastructure stages in the corona (#30). */
  readonly coronaStages: number;
}

/** Index points each source adds this cycle. */
export function instabilityGain(input: InstabilityInputs): SourceTotals {
  const r = INSTABILITY.perCycle;
  let asteroids = 0;
  let exotics = 0;
  for (const o of input.outposts) {
    const output = OUTPOSTS[o.kind].output;
    if (onAsteroid(input.system, o)) asteroids += output;
    if (o.good === 'exotics') exotics += output;
  }
  return {
    time: r.time,
    debris: input.debrisMetals * r.debrisMetal,
    asteroids: asteroids * r.asteroidUnit,
    exotics: exotics * r.exoticsUnit,
    corona: input.coronaStages * r.coronaStage,
  };
}

/** Adds a cycle's gain: the index stops at the maximum, the totals keep counting. */
export function addInstability(s: InstabilityState, gain: SourceTotals): InstabilityState {
  const add = (a: SourceTotals) =>
    Object.fromEntries(INSTABILITY_SOURCE_IDS.map((k) => [k, a[k] + gain[k]])) as SourceTotals;
  const sum = INSTABILITY_SOURCE_IDS.reduce((acc, k) => acc + gain[k], 0);
  return {
    ...s,
    value: Math.min(INSTABILITY.max, Math.round((s.value + sum) * 1e6) / 1e6),
    sinceCrisis: add(s.sinceCrisis),
    total: add(s.total),
  };
}

/** Thresholds at or below the index. */
export const thresholdsReached = (value: number): number =>
  INSTABILITY.thresholds.filter((t) => value >= t).length;

type RegularKind = Exclude<CrisisKindId, 'stellarPulsation'>;
const REGULAR = CRISIS_KIND_IDS.filter((k): k is RegularKind => k !== 'stellarPulsation');

/**
 * The regular crisis the director picks: each source pushes towards its kind,
 * time towards all alike, and the star weighs the result. A tie (time alone) is drawn by the
 * match RNG. Only geography and the rules matter: who is ahead never does.
 */
export function chooseCrisis(
  since: SourceTotals,
  star: StarClassId,
  rng: Rng,
): { readonly kind: RegularKind; readonly cause: InstabilitySourceId } {
  const score = Object.fromEntries(REGULAR.map((k) => [k, since.time / REGULAR.length])) as Record<
    RegularKind,
    number
  >;
  for (const [source, kind] of Object.entries(CRISIS_DIRECTOR.sourceKinds) as [
    Exclude<InstabilitySourceId, 'time'>,
    RegularKind,
  ][]) {
    score[kind] += since[source];
  }
  const weights = CRISIS_DIRECTOR.starWeights[star];
  const best = Math.max(...REGULAR.map((k) => score[k] * weights[k]));
  const top = REGULAR.filter((k) => score[k] * weights[k] >= best - 1e-9);
  const kind = top.length > 1 ? rng.pick(top) : top[0]!;
  let cause: InstabilitySourceId = 'time';
  for (const s of INSTABILITY_SOURCE_IDS) if (since[s] > since[cause]) cause = s;
  return { kind, cause };
}
